import classNames from 'classnames';
import React, {useCallback, useRef, useState} from 'react';

import {getSearchResultPropsForIndex} from 'notebook/mediocre/search/SearchResultProps';
import getColumnConstraintWithRowData from 'notebook/structured-tables/columns/getColumnConstraint';
import {SingleInputDragProps} from 'notebook/structured-tables/single-inputs/useSingleInputDragHandlers';
import {getLastSyncedUpstreamValueWithColumnConstraint} from 'notebook/structured-tables/util/getLastSyncedUpstreamValue';
import {TABLE_COLUMN} from 'notebook/tables/constants/TableSelectionConstants';
import ColumnHeaderAxisCell from 'notebook/tables/core-ui/ColumnHeaderAxisCell';
import TableCell from 'notebook/tables/core-ui/TableCell';
import {TableRowProps} from 'notebook/tables/core-ui/TableRow';
import {AxisCellModelProvider} from 'notebook/tables/store/contexts/AxisCellModelContext';
import {useRowModel} from 'notebook/tables/store/hooks/useRowModel';
import {useTableModel} from 'notebook/tables/store/hooks/useTableModel';
import {computeCellCid} from 'notebook/tables/store/logic/TableIdentityLogic';
import TableUtil from 'notebook/tables/TableUtil';
import getTestProps from 'test/lib/cypress/getTestProps';

const DEFAULT_SINGLE_INPUT_WIDTH = 150;

export interface TableSingleInputProps extends Partial<SingleInputDragProps> {
  j: number;
  rowProps: TableRowProps;
  columnWidth?: number;
  setColumnWidth?: (columnIndex: number, width: number) => void;
  locked?: boolean;
  dragIndex?: number;
  isDragging?: boolean;
  isDragOver?: boolean;
}

export default function TableSingleInput(props: TableSingleInputProps): JSX.Element {
  const i = 0;
  const {
    j,
    rowProps,
    columnWidth,
    setColumnWidth,
    locked,
    dragIndex,
    onDragStart,
    onDragOver,
    onDragEnd,
    isDragging,
    isDragOver,
    canDrag,
  } = props;
  const effectiveDragIndex = dragIndex ?? j;
  const {tableModel: columnHeader} = useTableModel((model) => model.columnHeader);
  const {viewer} = columnHeader;
  const rowModel = useRowModel();
  const {evaluatedRow, rowData, columnConstraints} = rowModel;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (locked || viewer || !setColumnWidth) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);

      const startX = e.clientX;
      const startWidth = containerRef.current?.offsetWidth || columnWidth || DEFAULT_SINGLE_INPUT_WIDTH;

      const handleMouseMove = (moveEvent: MouseEvent): void => {
        const delta = moveEvent.clientX - startX;
        const newWidth = Math.max(DEFAULT_SINGLE_INPUT_WIDTH, startWidth + delta);
        if (containerRef.current) {
          containerRef.current.style.width = `${newWidth}px`;
        }
      };

      const handleMouseUp = (upEvent: MouseEvent): void => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        const delta = upEvent.clientX - startX;
        const newWidth = Math.max(DEFAULT_SINGLE_INPUT_WIDTH, startWidth + delta);
        setColumnWidth(j, newWidth);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [locked, viewer, setColumnWidth, columnWidth, j]
  );

  const handleDragHandleMouseDown = useCallback((): boolean => Boolean(canDrag), [canDrag]);

  const renderHeader = (): JSX.Element => (
    <AxisCellModelProvider key={`axis-${j}`} dim={TABLE_COLUMN} index={j}>
      <ColumnHeaderAxisCell
        dataTestAttrs={{component: 'TableSingleInput', element: 'columnLabelCell'}}
        showDragHandle={Boolean(canDrag)}
        dragHandleMouseDown={handleDragHandleMouseDown}
      />
    </AxisCellModelProvider>
  );

  const renderCell = (): JSX.Element | null => {
    if (TableUtil.isNotRendered(i, j, evaluatedRow[j])) {
      return null;
    }

    const columnConstraint = getColumnConstraintWithRowData(columnConstraints[j], rowData);
    const lastSyncedUpstreamValue =
      getLastSyncedUpstreamValueWithColumnConstraint(rowData?.lastSyncedUpstreamValues, columnConstraint) ??
      undefined;

    return (
      <TableCell
        {...rowProps}
        {...{
          isSingleInputCell: true,
          cid: computeCellCid(rowModel.table.cid, i, j),

          i,
          j,
          lastSyncedUpstreamValue,
          isFirstColumn: false,
          hasRowBeenInView: true,
          isFillDownBoxLeftEdge: false,
          isFillDownBoxRightEdge: false,
          isFillDownBoxTopEdge: false,
          isFillDownBoxBottomEdge: false,
        }}
        {...getSearchResultPropsForIndex(rowProps, j)}
      />
    );
  };

  const canResize = !locked && !viewer && setColumnWidth != null;
  // if no width or less than the minimum width, defer to CSS to handle initial sizing (100% of container)
  const effectiveWidth = !columnWidth || columnWidth < DEFAULT_SINGLE_INPUT_WIDTH ? undefined : columnWidth;

  return (
    <div
      ref={containerRef}
      className={classNames('mediocre-tableEditable-singleInput', {
        'is-resizing': isResizing,
        'mediocre-tableEditable-singleInput--dragging': isDragging,
        'mediocre-tableEditable-singleInput--dragOver': isDragOver,
      })}
      style={{width: effectiveWidth}}
      onClick={(e) => {
        e.stopPropagation();
      }}
      draggable={canDrag}
      onDragStart={(e) => {
        e.stopPropagation();
        if (e.dataTransfer) {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', '');
        }
        onDragStart?.(effectiveDragIndex);
      }}
      onDrag={(e) => {
        e.stopPropagation();
      }}
      onDragEnter={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDragOver?.(effectiveDragIndex);
      }}
      onDragLeave={(e) => {
        e.stopPropagation();
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDragEnd={(e) => {
        e.stopPropagation();
        onDragEnd?.();
      }}
      {...getTestProps({component: 'SingleInput', element: 'root'})}
    >
      <table style={{borderCollapse: 'separate', width: '100%'}}>
        <tbody>
          <tr>{renderHeader()}</tr>
        </tbody>
      </table>
      <div className="mediocre-tableEditable-singleInput-cellWrapper">
        <table style={{borderCollapse: 'separate', width: '100%'}}>
          <tbody>
            <tr>{renderCell()}</tr>
          </tbody>
        </table>
        {canResize && (
          <div
            className="mediocre-tableEditable-singleInput-resizeHandle"
            onMouseDown={handleResizeMouseDown}
            {...getTestProps({component: 'SingleInput', element: 'resizeHandle'})}
          />
        )}
      </div>
    </div>
  );
}
